import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { DataTable, DataTableColumn, FilterBar, Notice, StatusPill } from '../patterns';
import { useMenu } from '../../auth/useMenu';
import { nutritionService, FertilizerProduct } from '../../services/nutritionService';
import NutritionProductForm from './NutritionProductForm';
import './nutrition.css';

const NutritionProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const canManage = useMenu('fertilizing:products');
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<FertilizerProduct[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<FertilizerProduct | undefined>();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    nutritionService
      .listFertilizers()
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // Deep-link from the Unit Review "Fix" link: `/nutrition/products?edit=<id>`
  // opens that fertilizer's edit form once the list has loaded (there is no
  // per-product route — the form is in-page state). We consume the param
  // (strip it, replace history) so a later cancel/back doesn't reopen it.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !canManage || rows.length === 0) return;
    const match = rows.find((r) => r._id === editId);
    if (match) {
      setEditing(match);
      setShowForm(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // setSearchParams is a stable ref (react-router v6); omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rows, canManage]);

  const handleDelete = async (product: FertilizerProduct) => {
    if (!window.confirm(t('nutrition.products.confirmDelete'))) return;
    try {
      await nutritionService.deleteFertilizer(product._id);
      setRows((prev) => prev.filter((r) => r._id !== product._id));
      setError(null);
    } catch {
      setError(t('nutrition.products.deleteError'));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.productName.toLowerCase().includes(q));
  }, [rows, search]);

  if (showForm) {
    return (
      <NutritionProductForm
        product={editing}
        onSave={() => {
          setShowForm(false);
          setEditing(undefined);
          load();
        }}
        onCancel={() => {
          setShowForm(false);
          setEditing(undefined);
        }}
      />
    );
  }

  const columns: DataTableColumn<FertilizerProduct>[] = [
    { key: 'name', label: t('nutrition.products.columns.name'), render: (r) => r.productName },
    {
      key: 'analysis',
      label: t('nutrition.products.columns.analysis'),
      render: (r) => `${r.analysis?.n ?? 0} · ${r.analysis?.p ?? 0} · ${r.analysis?.k ?? 0}`,
    },
    {
      key: 'methods',
      label: t('nutrition.products.columns.methods'),
      render: (r) =>
        (r.applicationMethods ?? []).map((m) => (
          <StatusPill key={m} variant="info">
            {t('nutrition.products.form.' + m)}
          </StatusPill>
        )),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) =>
        canManage ? (
          <span className="d-inline-flex gap-2">
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => {
                setEditing(r);
                setShowForm(true);
              }}
            >
              {t('common.edit')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              onClick={() => handleDelete(r)}
            >
              {t('common.delete')}
            </button>
          </span>
        ) : null,
    },
  ];

  return (
    <div data-testid="nutrition-products">
      <PageHeader
        title={t('nutrition.products.title')}
        meta={t('nutrition.products.meta', { count: rows.length })}
        actions={
          canManage ? (
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={() => {
                setEditing(undefined);
                setShowForm(true);
              }}
            >
              {t('nutrition.products.add')}
            </button>
          ) : null
        }
      />

      {error && (
        <Notice tone="danger" className="mb-3">
          {error}
        </Notice>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('nutrition.products.searchPlaceholder')}
      />

      <DataTable
        rows={filtered}
        rowKey={(r) => r._id}
        columns={columns}
        emptyState={t('nutrition.products.empty')}
      />
    </div>
  );
};

export default NutritionProductsPage;
